import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Phone as PhoneIcon, Add as AddIcon } from "@mui/icons-material";
import { voiceAgentAPI } from "../services/api";

// Metric Card Component
const MetricCard = ({ iconPath, value, label, valueColor }) => {
  return (
    <Card
      sx={{
        width: { xs: "calc(50% - 6px)", sm: "286px" },
        minWidth: { xs: "140px", sm: "286px" },
        height: "114px",
        paddingTop: { xs: "16px", sm: "25px" },
        paddingRight: { xs: "16px", sm: "25px" },
        paddingBottom: "1px",
        paddingLeft: { xs: "16px", sm: "25px" },
        borderRadius: "10px",
        border: "1px solid #E5E7EB",
        backgroundColor: "#FFFFFF",
        boxShadow:
          "0px 1px 2px -1px rgba(0, 0, 0, 0.1), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {/* Header with Label and Icon */}
        <Box
          sx={{
            width: "100%",
            height: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Arial",
              fontWeight: 400,
              fontStyle: "Regular",
              fontSize: { xs: "13px", sm: "16px" },
              lineHeight: "24px",
              letterSpacing: "0px",
              color: "#4A5565",
            }}
          >
            {label}
          </Typography>
        <Box
          sx={{
            width: "20px",
            height: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={iconPath}
            alt={label}
            style={{
              width: "20px",
              height: "20px",
            }}
          />
        </Box>
      </Box>

      {/* Value Container */}
      <Box
        sx={{
          width: "100%",
          height: "32px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Arial",
            fontWeight: 400,
            fontStyle: "Regular",
            fontSize: { xs: "24px", sm: "32px" },
            lineHeight: "32px",
            letterSpacing: "0px",
            color: valueColor,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Card>
  );
};

// Call History Card Component
const CallHistoryCard = ({ call, isSelected, onClick, isFirst }) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        width: "100%",
        minHeight: "auto",
        paddingTop: "17.07px",
        paddingRight: "17.07px",
        paddingBottom: "17.07px",
        paddingLeft: "17.07px",
        borderRadius: "10px",
        border: isFirst ? "1.07px solid #155DFC" : "1.07px solid #E5E7EB",
        backgroundColor: isFirst ? "#EFF6FF" : "#FFFFFF",
        cursor: "pointer",
        transition: "all 0.2s",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        boxShadow: "none",
        "&:hover": {
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      {/* Top section: Name, Company, Phone */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Arial",
            fontWeight: 600,
            fontStyle: "Regular",
            fontSize: "18px",
            lineHeight: "27px",
            letterSpacing: "0px",
            color: "#101828",
          }}
        >
          {call.name}
        </Typography>
      </Box>

      {/* Call Information Section */}
      <Box
        sx={{
          paddingTop: "12px",
          paddingRight: "12px",
          paddingLeft: "12px",
          paddingBottom: "12px",
          borderRadius: "8px",
          backgroundColor: "#F9FAFB",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <Box
          sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#6B7280",
              }}
            >
              Date:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#101828",
              }}
            >
              {call.date}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#6B7280",
              }}
            >
              Duration:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#101828",
              }}
            >
              {call.duration}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#6B7280",
              }}
            >
              Status:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#101828",
              }}
            >
              {call.status || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#6B7280",
              }}
            >
              End Reason:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#101828",
              }}
            >
              {call.rawData?.end_reason?.replace(/-/g, " ") || "N/A"}
            </Typography>
          </Box>
        </Box>
        {call.callId && (
          <Box>
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#6B7280",
              }}
            >
              Call ID:
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0px",
                color: "#101828",
              }}
            >
              {call.callId}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Transcript Section */}
      {call.transcript && call.transcript.length > 0 && (
        <Box
          sx={{
            paddingTop: "12px",
            paddingRight: "12px",
            paddingLeft: "12px",
            paddingBottom: "12px",
            borderRadius: "8px",
            backgroundColor: "#F9FAFB",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Arial",
              fontWeight: 600,
              fontStyle: "Regular",
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "0px",
              color: "#364153",
            }}
          >
            Transcript:
          </Typography>
          <Typography
            sx={{
              fontFamily: "Consolas",
              fontWeight: 400,
              fontStyle: "Regular",
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "0px",
              color: "#364153",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              maxHeight: "200px",
              overflow: "auto",
            }}
          >
            {call.transcript.map((entry, index) => (
              <span key={index}>
                <strong>{entry.speaker}:</strong> {entry.text}
                {"\n\n"}
              </span>
            ))}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

const VoiceAgent = () => {
  const [selectedCall, setSelectedCall] = useState(null);
  const [calls, setCalls] = useState([]);
  const [metrics, setMetrics] = useState([
    {
      iconPath: "/IconP.svg",
      value: 0,
      label: "Total Calls",
      valueColor: "#155DFC",
    },
    {
      iconPath: "/IconC.svg",
      value: 0,
      label: "Completed",
      valueColor: "#00A63E",
    },
    {
      iconPath: "/IconPT.svg",
      value: 0,
      label: "Decision Makers",
      valueColor: "#9810FA",
    },
    {
      iconPath: "/IconC.svg",
      value: 0,
      label: "Qualified",
      valueColor: "#00A63E",
    },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch calls on component mount
  useEffect(() => {
    fetchCalls();
  }, []);

  const fetchCalls = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await voiceAgentAPI.getCalls();
      console.log("Voice Agent API Response:", response);

      // Map API response to calls format
      const mappedCalls = mapApiResponseToCalls(response);
      setCalls(mappedCalls);

      // Calculate metrics from calls
      calculateMetrics(mappedCalls);

      // Auto-select first call if available
      if (mappedCalls.length > 0 && !selectedCall) {
        setSelectedCall(mappedCalls[0]);
      }
    } catch (err) {
      console.error("Error fetching calls:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to fetch calls"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const mapApiResponseToCalls = (apiResponse) => {
    // Handle different response structures - API returns array directly
    const callsData = Array.isArray(apiResponse)
      ? apiResponse
      : apiResponse.data || apiResponse.calls || [];

    return callsData.map((call) => {
      // Map API response fields to UI format based on actual API structure
      const status = call.status || "Pending";

      // Map end_reason to outcome
      let outcome = "Pending";
      if (call.end_reason) {
        if (call.end_reason === "customer-ended-call") {
          outcome = "Completed";
        } else if (call.end_reason === "customer-did-not-answer") {
          outcome = "No Answer";
        } else if (
          call.end_reason === "failed" ||
          call.end_reason === "error"
        ) {
          outcome = "Failed";
        } else {
          outcome = call.end_reason
            .replace(/-/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
        }
      }

      // Format duration
      const duration = formatDuration(call.duration || 0);

      // Qualified status - can be determined from transcript or other fields
      const qualified = call.qualified || "No";

      // Decision maker - not in API response, default to false
      const isDecisionMaker = false;

      // Format date from created_at
      const date =
        call.created_at || call.inserted_at || new Date().toISOString();
      const formattedDate = new Date(date).toLocaleString();

      // Parse transcript - API returns transcript as string
      let mappedTranscript = [];
      if (call.transcript) {
        // Transcript is a string, parse it into speaker/text pairs
        const transcriptLines = call.transcript
          .split("\n")
          .filter((line) => line.trim());
        mappedTranscript = transcriptLines.map((line) => {
          // Format: "AI: text" or "User: text"
          const colonIndex = line.indexOf(":");
          if (colonIndex > 0) {
            const speaker = line.substring(0, colonIndex).trim();
            const text = line.substring(colonIndex + 1).trim();
            return {
              speaker: speaker === "AI" ? "Agent" : speaker,
              text: text,
            };
          }
          return {
            speaker: "Agent",
            text: line.trim(),
          };
        });
      }

      return {
        id: call.id || call.call_id || Math.random(),
        name: call.person_id || call.customer_number || "Unknown",
        company: call.organisation_id || "N/A",
        phone: call.customer_number || "Unknown",
        date: formattedDate,
        outcome: outcome,
        duration: duration,
        qualified: qualified,
        status: status,
        isDecisionMaker: isDecisionMaker,
        recordingUrl: "#", // Not in API response
        currentTime: "0:00",
        totalTime: duration,
        nextSteps: call.end_reason
          ? `Call ended: ${call.end_reason.replace(/-/g, " ")}`
          : "No next steps defined",
        transcript: mappedTranscript,
        // Store additional API fields
        provider: call.provider || "N/A",
        callId: call.call_id || "",
        assistantId: call.assistant_id || "",
        phoneNumberId: call.phone_number_id || "",
        rawData: call,
      };
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const calculateMetrics = (callsData) => {
    const totalCalls = callsData.length;
    const completed = callsData.filter(
      (call) => call.status === "Completed"
    ).length;
    const decisionMakers = callsData.filter(
      (call) => call.isDecisionMaker
    ).length;
    const qualified = callsData.filter(
      (call) => call.qualified === "Yes"
    ).length;

    setMetrics([
      {
        iconPath: "/IconP.svg",
        value: totalCalls,
        label: "Total Calls",
        valueColor: "#155DFC",
      },
      {
        iconPath: "/IconC.svg",
        value: completed,
        label: "Completed",
        valueColor: "#00A63E",
      },
      {
        iconPath: "/IconPT.svg",
        value: decisionMakers,
        label: "Decision Makers",
        valueColor: "#9810FA",
      },
      {
        iconPath: "/IconC.svg",
        value: qualified,
        label: "Qualified",
        valueColor: "#00A63E",
      },
    ]);
  };

  const handleCallClick = (call) => {
    setSelectedCall(call);
  };

  const formatTime = (timeString) => {
    // Convert "1:23" format to seconds for progress calculation
    const parts = timeString.split(":");
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  };

  const calculateProgress = (current, total) => {
    if (total === "0:00") return 0;
    const currentSeconds = formatTime(current);
    const totalSeconds = formatTime(total);
    return (currentSeconds / totalSeconds) * 100;
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Scrollable Container - Header and Content together */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          overflowX: "hidden",
          minHeight: 0,
          paddingBottom: { 
            xs: "calc(60px + env(safe-area-inset-bottom, 0px))", 
            sm: "calc(40px + env(safe-area-inset-bottom, 0px))", 
            md: "0px" 
          },
          WebkitOverflowScrolling: "touch",
          marginBottom: { xs: "0px", md: "0px" },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "1216px",
            margin: "0 auto",
            padding: { xs: "16px 16px 0 16px", sm: "24px 24px 0 24px" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: "16px", sm: "24px" },
          }}
        >
          <Box
            sx={{
              width: "100%",
              minHeight: "56px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Arial",
                  fontWeight: 400,
                  fontStyle: "Regular",
                  fontSize: { xs: "18px", sm: "20px", md: "24px" },
                  lineHeight: "24px",
                  letterSpacing: "0px",
                  color: "#101828",
                }}
              >
                Voice Agent Integration
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Arial",
                  fontWeight: 400,
                  fontStyle: "Regular",
                  fontSize: { xs: "13px", sm: "14px", md: "16px" },
                  lineHeight: "24px",
                  letterSpacing: "0px",
                  color: "#4A5565",
                }}
              >
                Manage AI-powered voice outreach and call analytics
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content Area */}
        <Box sx={{ padding: { xs: "16px", sm: "24px" }, width: "100%", maxWidth: "1216px", margin: "0 auto" }}>
          {/* Metrics Cards */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: "12px", sm: "24px" },
              mb: 4,
              flexWrap: "wrap",
            }}
          >
            {metrics.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: "100px"}}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {/* Call History Heading */}
                <Typography
                  sx={{
                    fontFamily: "Arial",
                    fontWeight: 400,
                    fontStyle: "Regular",
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    color: "#101828",
                  }}
                >
                  Call History
                </Typography>

                {/* Call History Cards */}
                {calls.length === 0 ? (
                  <Card
                    sx={{
                      padding: "24px",
                      textAlign: "center",
                      borderRadius: "10px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <PhoneIcon
                      sx={{ fontSize: "64px", color: "#D1D5DB", mb: 2 }}
                    />
                    <Typography sx={{ color: "#9CA3AF", fontSize: "16px" }}>
                      No calls found. Make your first call to get started.
                    </Typography>
                  </Card>
                ) : (
                  calls.map((call, index) => (
                    <CallHistoryCard
                      key={call.id}
                      call={call}
                      isSelected={selectedCall?.id === call.id}
                      onClick={() => handleCallClick(call)}
                      isFirst={index === 0}
                    />
                  ))
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VoiceAgent;
